import { useAssessmentContext } from '../context';
import IAnswer from '../models/IAnswer';
import IAssessmentElement from '../models/IAssessmentElement';
import ISchemaItems from '../models/ISchemaItems';
import { IValidatorRule } from '../models/IValidator';

import { ValidationRuleHelper } from './Validation/ValidationRuleHelper';
import AssessmentHelpers from './AssessmentHelpers';
import AssessmentLocalStorageManager from './AssessmentLocalStorageManager';
import Common from './Common';

export default function useQuestionManager() 
{
	const assessmentContext = useAssessmentContext();

	const getSchemaItems = (question: IAssessmentElement): ISchemaItems | null => 
	{
		let schemaItems = null;

		const schemaProperties = question.validationSchema?.properties;
		if (schemaProperties)
			schemaItems = schemaProperties['value']?.items as ISchemaItems;

		if (!schemaItems)
			throw new Error(`Question ${question.id} has a malformed validation schema`);

		return schemaItems;
	}

	const updateAnswerValue = (questionId: string, answerValues: string[]) => 
	{
		answerValues = answerValues.filter((i) => !Common.isNullOrWhiteSpace(i));

		const updatedAnswer = {
			id: questionId,
			value: answerValues
		} as IAnswer

		assessmentContext.setAnswer(updatedAnswer);
	}

	const getQuestionValidatorRules = (question: IAssessmentElement) => 
	{
		const validatorRules = [] as IValidatorRule[];
		if (question.validatorRules)
			validatorRules.push(...question.validatorRules)
		if (question.validationSchema)
			validatorRules.push(ValidationRuleHelper.question(question))

		return validatorRules;
	}

	const showElement = (showConditionSource: string, showConditionValue: string): boolean => 
	{
		const existingAssessment = assessmentContext.assessment;
		if (!existingAssessment)
			return false

		const allElements = AssessmentHelpers.getAllAssessmentQuestions(existingAssessment);
		const allConditionsAreMet = areConditionsMet(existingAssessment.assessmentId, allElements, showConditionSource, showConditionValue)
		return allConditionsAreMet;
	}

	const areConditionsMet = (assessmentId: string, allElements: IAssessmentElement[], showConditionSource: string, showConditionValue: string): boolean => 
	{
		let allConditionsAreMet = true;
		if (showConditionSource?.length > 0 && showConditionValue?.length > 0) 
		{
			const parentQuestion = allElements.filter(q => q.referenceId == showConditionSource)[0];
			const answerValues = AssessmentLocalStorageManager.getAnswerValues(assessmentId, parentQuestion.id);
			allConditionsAreMet = false;
			const regexp = new RegExp(showConditionValue)
			if (answerValues.length == 0)
				allConditionsAreMet = regexp.test('')
			else 
			{
				for (let i = 0; i < answerValues.length; i++) 
				{
					const value = answerValues[i]
					if (regexp.test(value)) 
					{
						allConditionsAreMet = true
						break;
					}
				}
			}

			//If conditions are met we then traverse up all dependent parents check each is also met
			if (allConditionsAreMet)
				allConditionsAreMet = areConditionsMet(assessmentId, allElements, parentQuestion.showConditionSource ?? '', parentQuestion.showConditionValue ?? '')

		}

		return allConditionsAreMet;
	}

	const getAllChildElementReferences = (parentElement: IAssessmentElement, allElements: IAssessmentElement[]): string[] => 
	{
		const allChildReferences = getChildren(allElements, parentElement)
		return allChildReferences;
	}

	const getChildren = (allElements: IAssessmentElement[], parentElement: IAssessmentElement): string[] => 
	{
		let children = [] as string[];
		if (parentElement.hasConditionalChildren) 
		{
			const childElements = allElements.filter(q => q.showConditionSource == parentElement.referenceId);
			children = children.concat(childElements.map(x => x.referenceId))

			for (let i = 0; i < childElements.length; i++)
				children = children.concat(getChildren(allElements, childElements[i]))
		}

		return children;
	}

	return {
		getSchemaItems,
		updateAnswerValue,
		getQuestionValidatorRules,
		showElement,
		areConditionsMet,
		getAllChildElementReferences,
		getChildren
	} as const;
}
